import {useService} from "../../../../../base/hooks/useService";
import OrganisationsService from "../../../../../services/OrganisationsService";
import ToasterService from "../../../../../services/ToastService";
import {useLoading} from "../../../../../base/hooks/useLoading";
import {useModal} from "../../../../../base/hooks/useModal";
import React, {useEffect, useState} from "react";
import _ from "lodash";
import joinClassNames from "../../../../../base/helpers/joinClassNames";
import {ModalFooter, Spinner} from "reactstrap";
import {Formik} from "formik";
import Button from "../../../../../base/components/Button";
import {BUTTON_COLORS} from "../../../../../base/components/Button/appearance";
import {SELECT_ALL_ID} from "../../../Admins/RoleForm/constants";
import Checkbox from "../../../../../base/components/Checkbox";

const EditServicesModal = ({isOpen, onClose, organisation}) => {
    /**
     * @type {OrganisationsService}
     */
    const organisationService = useService(OrganisationsService);
    /**
     * @type {ToasterService}
     */
    const toastService = useService(ToasterService);

    const [isLoading, {registerPromise}] = useLoading(false);
    const {Modal} = useModal();

    const [services, updateServices] = useState([])

    const updateService = (values, formikHelpers) => {
        formikHelpers.setSubmitting(true);
        const data = values.data?.filter((service) => service.isEnabled).map((item) => item.id)
        registerPromise(organisationService.editServicesForOrganisation(organisation.id, {data}))
            .then(() => {
                toastService.success(`Organisation services have been successfully updated`)
                onClose()
            })
            .catch((error) => {
                toastService.error(error.message);
            })
            .finally(() => {
                formikHelpers.setSubmitting(false);
            })
    }

    const logo = organisation?.files?.[0]

    const getServices = () => {
        const paramsNotEnabled = {
            isEnabled: false
        }
        const paramsEnabled = {
            isEnabled: true
        }
        registerPromise(Promise.all([
            registerPromise(organisationService.getServicesListForOrganisations(organisation.id, paramsNotEnabled)),
            registerPromise(organisationService.getServicesListForOrganisations(organisation.id, paramsEnabled))
        ])).then(([dataNotEnabled, dataEnabled]) => {
            const fullList = [
                ...dataNotEnabled?.data ?? [],
                ...dataEnabled?.data ?? [],
            ]
            const sortedFullList = _.chain(fullList)
                    .sortBy('id')
                    .value();
            updateServices(sortedFullList)
        })
    }

    useEffect(() => {
        getServices()
    }, [])

    return(
        <Modal isOpen={isOpen} toggle={() => onClose()} size="md"
               className="fontStyles" >
            <Modal.Header onClose={() => onClose()} className="font-weight-semibold text-dark">
                <div className="d-flex gap-3">
                    {logo?.link && <div className="bg-light-gray rounded-1 p-1 d-flex align-items center justify-content-center">
                        <img src={logo?.link} alt="Organisation logo" className="organisation-logo"/>
                    </div>}
                    <div className="d-flex flex-column">
                        <p className="text-current mb-1 font-weight-semibold font-size-15">{organisation?.name}</p>
                        <p className="text-secondary font-size-13 mb-0 font-weight-400">{organisation?.domain}</p>
                    </div>
                </div>
            </Modal.Header>
            <Formik
                initialValues={{
                    data: services
                }}
                validateOnBlur
                onSubmit={(values, formikHelpers) => updateService(values, formikHelpers)}
                enableReinitialize
            >
                {({values, handleSubmit, isSubmitting, setFieldValue}) => {
                    return (
                        <div className="position-relative">
                            {!values?.data?.length ? <section className="position-relative w-100 h-25 p-5 spinner d-flex align-items-center justify-content-center">
                                <Spinner color="info"/>
                            </section> : <form
                                className={joinClassNames("form-horizontal", isSubmitting && "pointer-events-none")}
                                onSubmit={handleSubmit}
                            >
                                <Modal.Body>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <p className="text-current mb-0 font-weight-semibold font-size-15">Available services</p>
                                        <div
                                            className="text-danger cursor-pointer mb-0"
                                            onClick={() => {
                                                values?.data?.map((service, index) => {
                                                    setFieldValue(`data.${index}.isEnabled`, false)
                                                })
                                            }}
                                        >Clear</div>
                                    </div>
                                    <Checkbox
                                        id={SELECT_ALL_ID}
                                        text="Full access"
                                        value={!!values.data?.every((service) => service.isEnabled === true)}
                                        onChange={(event) => {
                                            event.stopPropagation();
                                            const value = event.target.checked
                                            if (!value) {
                                                values?.data?.map((service, index) => {
                                                    setFieldValue(`data.${index}.isEnabled`, false)
                                                })
                                            } else {
                                                values?.data?.map((service, index) => {
                                                    setFieldValue(`data.${index}.isEnabled`, true)
                                                })
                                            }
                                        }}
                                        key={SELECT_ALL_ID}
                                        className="py-2 mb-0 edit-service-checkbox"
                                    />
                                    <div className="separator-block" />
                                    <div>
                                        {values?.data?.map((service, index) => {
                                            return <Checkbox
                                                id={service.id}
                                                text={service.name}
                                                value={service.isEnabled}
                                                onChange={(event) => {
                                                    event.stopPropagation();
                                                    const value = event.target.checked
                                                    setFieldValue(`data.${index}.isEnabled`, value)
                                                }}
                                                key={service.id}
                                                className="py-2 mb-0 edit-service-checkbox"
                                            />
                                        })}
                                    </div>
                                </Modal.Body>
                                <ModalFooter>
                                    <div className="d-flex justify-content-end gap-2">
                                        <Button
                                            color={BUTTON_COLORS.primaryOutline}
                                            type="button"
                                            onClick={onClose}
                                        >
                                            Cancel
                                        </Button>

                                        <Button
                                            color={BUTTON_COLORS.primary}
                                            type="submit"
                                            disabled={isSubmitting || values.data?.every((service) => service.isEnabled === false)}
                                            loading={isLoading}
                                        >
                                            Save changes
                                        </Button>
                                    </div>
                                </ModalFooter>
                            </form>}
                        </div>
                    )
                }}
            </Formik>
        </Modal>
    )
}

export default EditServicesModal